/**
 * Currency Column Pattern
 * 
 * Creates currency columns with proper formatting and locale support.
 */

import type { ColumnDef } from '@tanstack/react-table';
import { TABLE_SETTINGS } from '../../constants/tableSettings';
import type { CurrencyColumnOptions } from './types';

/**
 * Creates a currency column with smart defaults and locale formatting
 * 
 * @param accessor - The data accessor key
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // Basic usage - uses default USD formatting
 * currencyColumn('amount')
 * 
 * // With customization
 * currencyColumn('price', {
 *   currency: 'EUR',
 *   precision: 3,
 *   showSymbol: false
 * })
 */
export function currencyColumn<T>(
  accessor: keyof T,
  options: CurrencyColumnOptions = {}
): ColumnDef<T> {
  const {
    header = String(accessor),
    sortable = true,
    resizable = true,
    currency = TABLE_SETTINGS.currency.default,
    locale = TABLE_SETTINGS.currency.locale,
    precision = TABLE_SETTINGS.currency.precision,
    showSymbol = TABLE_SETTINGS.currency.showSymbol,
    emptyDisplay = TABLE_SETTINGS.text.emptyDisplay,
    width,
    maxWidth,
    minWidth,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    meta: {
      className: 'text-right', // Right-justify numbers/currency
    },
    cell: ({ row }) => {
      const amount = row.original[accessor] as number;
      
      // Handle null/undefined amounts
      if (amount == null || amount === undefined) {
        return emptyDisplay;
      }
      
      // Handle invalid numbers
      if (typeof amount !== 'number' || isNaN(amount)) {
        return emptyDisplay;
      }
      
      try {
        return new Intl.NumberFormat(locale, {
          style: showSymbol ? 'currency' : 'decimal',
          currency: currency,
          minimumFractionDigits: precision,
          maximumFractionDigits: precision,
        }).format(amount);
      } catch {
        // Fallback for invalid locale/currency
        return showSymbol 
          ? `${currency} ${amount.toFixed(precision)}`
          : amount.toFixed(precision);
      }
    },
  };
}

/**
 * Creates a simple currency column with USD formatting
 */
export function simpleCurrencyColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return currencyColumn(accessor, {
    header,
    currency: 'USD',
    showSymbol: true,
  });
}

/**
 * Creates a number column with comma separators (no currency symbol)
 */
export function numberColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return currencyColumn(accessor, {
    header,
    showSymbol: false,
    precision: 0,
  });
}

/**
 * Creates a percentage column
 */
export function percentageColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return {
    id: String(accessor),
    header: header || String(accessor),
    accessorKey: accessor,
    enableSorting: true,
    cell: ({ row }) => {
      const value = row.original[accessor] as number;
      
      if (value == null || typeof value !== 'number' || isNaN(value)) {
        return TABLE_SETTINGS.text.emptyDisplay;
      }
      
      return `${(value * 100).toFixed(1)}%`;
    },
  };
} 